<template>
    <div class="kyc-return">
        <div class="kyc-card">

            <!-- Esperando resultado -->
            <HyFLoader v-if="state === 'checking'" variant="inline">
                <h2>Verificando tu identidad</h2>
                <p>Estamos confirmando los datos con nuestro proveedor. Esto puede demorar unos segundos.</p>
                <p class="kyc-hint">No cierres esta ventana.</p>
            </HyFLoader>

            <!-- Aprobado, confirmando la reserva -->
            <HyFLoader v-else-if="state === 'confirming'" key="confirming" variant="inline">
                <h2>Identidad verificada</h2>
                <p>Estamos confirmando tu reserva…</p>
            </HyFLoader>

            <!-- Todo listo -->
            <template v-else-if="state === 'done'">
                <div class="kyc-icon kyc-icon--ok">✓</div>
                <h2>¡Reserva enviada!</h2>
                <p>Tu identidad quedó verificada y la solicitud de reserva ya fue enviada al anfitrión.</p>
                <button class="kyc-btn" @click="goToReservations">Ver mis reservas</button>
            </template>

            <!-- Verificado, pero no había reserva pendiente que retomar -->
            <template v-else-if="state === 'verified'">
                <div class="kyc-icon kyc-icon--ok">✓</div>
                <h2>Identidad verificada</h2>
                <p>Ya podés completar tu reserva.</p>
                <button class="kyc-btn" @click="goBackToReservation">Continuar</button>
            </template>

            <!-- Revisión manual -->
            <template v-else-if="state === 'in_review'">
                <div class="kyc-icon kyc-icon--wait">⏳</div>
                <h2>Estamos revisando tus documentos</h2>
                <p>
                    La verificación quedó en revisión manual. Te avisamos por correo apenas se complete,
                    y ahí vas a poder confirmar tu reserva.
                </p>
                <p class="kyc-hint">Tu reserva quedó guardada, no se perdió.</p>
                <button class="kyc-btn" @click="goToReservations">Ver mis reservas</button>
            </template>

            <!-- Rechazado o incompleto -->
            <template v-else>
                <div class="kyc-icon kyc-icon--error">!</div>
                <h2>No pudimos verificar tu identidad</h2>
                <p>{{ errorMessage }}</p>
                <button class="kyc-btn" :disabled="retrying" @click="retryVerification">
                    {{ retrying ? 'Abriendo…' : 'Reintentar verificación' }}
                </button>
                <button class="kyc-btn kyc-btn--ghost" @click="goToReservations">Ver mis reservas</button>
            </template>

        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import HyFLoader from '@/components/Loader/HyFLoader.vue';

// Didit puede tardar en resolver: hacemos polling en vez de asumir el resultado
// del redirect, que no es confiable.
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // ~2 minutos

export default {
    name: "IdentityVerificationReturn",
    components: { HyFLoader },

    data() {
        return {
            state: "checking", // checking | confirming | done | verified | in_review | error
            errorMessage: "La verificación no se completó. Puedes intentarlo de nuevo.",
            retrying: false,
            polls: 0,
            timer: null,
            pending: null, // { idreserva, coupon, returnPath }
        };
    },

    created() {
        this.pending = this.readPendingReservation();
        this.checkStatus();
    },

    beforeDestroy() {
        this.clearTimer();
    },

    methods: {
        ...mapActions(["fetchKycStatus", "makeReservationRequest", "createDiditSession"]),
        ...mapActions("search", ["setCheckInDate", "setCheckOutDate"]),
        ...mapActions("simulator", ["setInitDate", "setEndDate"]),

        /**
         * Convierte 'YYYY-MM-DD' a un Date en hora local.
         *
         * Importante: las acciones del store pasan el valor por formatDate(), que
         * al recibir un string lo interpreta como UTC y luego lee el día en hora
         * local. En zonas con offset negativo (Chile) eso corre la fecha un día
         * atrás. Pasando un Date construido local, el ida y vuelta es exacto.
         */
        toLocalDate(value) {
            if (!value) {
                return null;
            }

            const parts = String(value).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                return null;
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        },

        /**
         * Repone en el store las fechas que se perdieron al salir del sitio.
         * El id del alojamiento no hace falta: lo repone Simulator.vue desde
         * los params de la ruta al montarse.
         */
        restoreReservationContext(pending) {
            const checkIn = this.toLocalDate(pending.checkInDate);
            const checkOut = this.toLocalDate(pending.checkOutDate);

            if (!checkIn || !checkOut) {
                return false;
            }

            this.setCheckInDate(checkIn);
            this.setInitDate(checkIn);
            this.setCheckOutDate(checkOut);
            this.setEndDate(checkOut);

            return true;
        },

        readPendingReservation() {
            try {
                const raw = sessionStorage.getItem("hyf_kyc_pending");
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        },

        clearPendingReservation() {
            try {
                sessionStorage.removeItem("hyf_kyc_pending");
            } catch (e) {
                // sin sessionStorage no hay nada que limpiar
            }
        },

        clearTimer() {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
        },

        checkStatus() {
            this.polls += 1;

            this.fetchKycStatus()
                .then((data) => {
                    if (data.status === "approved") {
                        this.onApproved();
                        return;
                    }

                    if (data.status === "in_review") {
                        this.state = "in_review";
                        return;
                    }

                    if (data.status === "declined") {
                        this.state = "error";
                        this.errorMessage = data.reason
                            ? "La verificación fue rechazada: " + data.reason
                            : "La verificación fue rechazada. Revisa que el documento sea legible y esté vigente.";
                        return;
                    }

                    // Todavía sin resolución: el webhook puede no haber llegado.
                    if (this.polls >= MAX_POLLS) {
                        this.state = "error";
                        this.errorMessage =
                            "Todavía no recibimos el resultado. Si ya completaste la verificación, " +
                            "esperá unos minutos y volvé a entrar desde tus reservas.";
                        return;
                    }

                    this.timer = setTimeout(this.checkStatus, POLL_INTERVAL_MS);
                })
                .catch(() => {
                    this.state = "error";
                    this.errorMessage = "No pudimos consultar el estado de tu verificación. Intentá nuevamente.";
                });
        },

        onApproved() {
            const pending = this.pending;

            // Caso normal: la verificación se pidió ANTES de crear la reserva.
            // Restauramos fechas y devolvemos al usuario al flujo de siempre,
            // que se reabre solo en Pay.vue.
            if (pending && pending.mode === "pre-reservation" && pending.returnPath) {
                const restored = this.restoreReservationContext(pending);
                this.clearPendingReservation();

                if (restored) {
                    try {
                        sessionStorage.setItem("hyf_kyc_resume", "1");
                    } catch (e) {
                        // sin sessionStorage vuelve a la ficha y reserva a mano
                    }
                }

                this.$router.push(pending.returnPath);
                return;
            }

            // Sin reserva pendiente (por ejemplo, verificó desde su perfil)
            if (!pending || !pending.idreserva) {
                this.state = "verified";
                return;
            }

            this.state = "confirming";

            this.makeReservationRequest({
                idreserva: this.pending.idreserva,
                coupon: this.pending.coupon,
            })
                .then(() => {
                    this.clearPendingReservation();
                    this.state = "done";
                })
                .catch((error) => {
                    const data = error.response ? error.response.data : null;

                    // El KYC pasó pero el backend rechazó por otro motivo
                    // (fechas ya tomadas, reserva cancelada, etc.)
                    this.state = "error";
                    this.errorMessage =
                        (data && data.message)
                            ? data.message
                            : "Tu identidad quedó verificada, pero no pudimos confirmar la reserva. Revisa tus reservas.";
                    this.clearPendingReservation();
                });
        },

        retryVerification() {
            this.retrying = true;

            this.createDiditSession({
                idreserva: this.pending ? this.pending.idreserva : null,
            })
                .then((data) => {
                    if (data.url) {
                        window.location.href = data.url;
                        return;
                    }
                    // Ya estaba verificado
                    this.retrying = false;
                    this.onApproved();
                })
                .catch(() => {
                    this.retrying = false;
                    this.errorMessage = "No pudimos iniciar la verificación. Intentá en unos minutos.";
                });
        },

        goToReservations() {
            this.$router.push({ name: "reservations-made" });
        },

        goBackToReservation() {
            if (this.pending && this.pending.returnPath) {
                this.$router.push(this.pending.returnPath);
                return;
            }
            this.goToReservations();
        },
    },
};
</script>

<style scoped>
.kyc-return {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70vh;
    padding: 24px;
}

.kyc-card {
    max-width: 460px;
    width: 100%;
    text-align: center;
    padding: 40px 32px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
}

.kyc-card h2 {
    font-size: 22px;
    margin: 20px 0 12px;
}

.kyc-card p {
    color: #555;
    line-height: 1.55;
    margin: 0 0 12px;
}

.kyc-hint {
    font-size: 13px;
    color: #888;
}

.kyc-spinner {
    width: 44px;
    height: 44px;
    margin: 0 auto;
    border: 3px solid #e6e6e6;
    border-top-color: #ff5a5f;
    border-radius: 50%;
    animation: kyc-spin 0.9s linear infinite;
}

@keyframes kyc-spin {
    to { transform: rotate(360deg); }
}

.kyc-icon {
    width: 56px;
    height: 56px;
    line-height: 56px;
    margin: 0 auto;
    border-radius: 50%;
    font-size: 26px;
    font-weight: 700;
    color: #fff;
}

.kyc-icon--ok    { background: #2eb872; }
.kyc-icon--wait  { background: #f0ad4e; }
.kyc-icon--error { background: #d9534f; }

.kyc-btn {
    display: block;
    width: 100%;
    margin-top: 16px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    background: #ff5a5f;
    color: #fff;
    font-size: 15px;
    cursor: pointer;
}

.kyc-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.kyc-btn--ghost {
    background: transparent;
    color: #555;
    border: 1px solid #ddd;
}
</style>
